/**
 * PaymentSplit API client.
 *
 * All six customer-plane split routes go through the online-ordering BFF
 * proxy (spec §5) — NOT payment_service directly. The BFF derives the
 * claimer identity from the caller's token and forwards X-Caller-Role
 * headers; this client therefore never sends claimedBy* fields.
 *
 *   - GET   /v1/online-ordering/payments/orders/{orderId}/splits
 *   - POST  /v1/online-ordering/payments/splits
 *   - PATCH /v1/online-ordering/payments/splits/{splitId}
 *   - POST  /v1/online-ordering/payments/splits/{splitId}/checkout
 *   - POST  /v1/online-ordering/payments/splits/{splitId}/mark-cash-pending
 *   - POST  /v1/online-ordering/payments/splits/{splitId}/release
 *
 * Staff-only routes (collect-cash, confirm) are deliberately NOT proxied to
 * the customer plane and have no client here.
 *
 * The /v1/online-ordering prefix means apiFetch auto-attaches X-API-KEY and
 * the payer/participant token plumbing applies (see api/http.ts).
 */

import { ApiError, apiFetch } from "./http";

const BASE = "/v1/online-ordering/payments";

export type SplitState =
  | "draft"
  | "awaiting_collection"
  | "processing"
  | "paid"
  | "released"
  | "failed"
  | "refunded";

export type SplitMethod = "card" | "cash";

export interface ClaimedLine {
  lineId: string;
  numerator: number;
  denominator: number;
}

export interface PaymentSplit {
  splitId: string;
  tenantId: string;
  branchId: string;
  orderId: string;
  state: SplitState;
  method: SplitMethod;
  claimedLines: ClaimedLine[];
  subtotalAmount: number;
  taxAmount: number;
  totalAmount: number;
  tipAmount: number;
  shortCode: string | null;
  paymentIds: string[];
  claimedByGuestId: string | null;
  claimedByCustomerId: string | null;
  /** Server-stamped payer display name (spec §6) — null on legacy splits. */
  claimedByDisplayName?: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CoverageReport {
  fullyCovered: boolean;
  uncoveredLineIds: string[];
  overCoveredLineIds: string[];
}

export interface ListSplitsResponse {
  splits: PaymentSplit[];
  coverage: CoverageReport;
}

export interface CreateSplitInput {
  branchId: string;
  orderId: string;
  claimedLines: ClaimedLine[];
  method: SplitMethod;
}

export interface CheckoutCardInput {
  /** Optional: payment_service falls back to the tenant's first active
   *  card-type provider config when omitted. */
  configurationId?: string;
  currency: string;
  paymentMethodId?: string;
  providerKey?: string;
  methodType?: string;
  returnUrl?: string;
  urlPath?: string;
  savedCardId?: string;
  idempotencyKey?: string;
  customer?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface IntentResponse {
  status: string;
  clientSecret?: string | null;
  transactionRef?: string | null;
  intentId?: string;
  amount: number;
  currency: string;
  failureReason?: string | null;
  metadata?: Record<string, unknown> | null;
  urlPath?: string | null;
  returnUrl?: string | null;
}

export interface OverCoverageDetail {
  overCoveredLineIds: string[];
}

export interface UncoveredDetail {
  uncoveredLineIds: string[];
}

/** 409 Conflict raised when the create/update would push lines past 1.0. */
export class PaymentSplitOverCoverage extends Error {
  readonly overCoveredLineIds: string[];
  constructor(detail: OverCoverageDetail) {
    super(`Over-covered lines: ${detail.overCoveredLineIds.join(", ")}`);
    this.name = "PaymentSplitOverCoverage";
    this.overCoveredLineIds = detail.overCoveredLineIds;
  }
}

/** 409 Conflict raised when a card checkout would still leave the order short. */
export class PaymentSplitUncovered extends Error {
  readonly uncoveredLineIds: string[];
  constructor(detail: UncoveredDetail) {
    super(`Uncovered lines: ${detail.uncoveredLineIds.join(", ")}`);
    this.name = "PaymentSplitUncovered";
    this.uncoveredLineIds = detail.uncoveredLineIds;
  }
}

function map409(err: unknown): never {
  if (err instanceof ApiError && err.status === 409 && err.body) {
    const detail = (err.body as { detail?: unknown }).detail ?? err.body;
    if (detail && typeof detail === "object") {
      if ("overCoveredLineIds" in detail) {
        throw new PaymentSplitOverCoverage(detail as OverCoverageDetail);
      }
      if ("uncoveredLineIds" in detail) {
        throw new PaymentSplitUncovered(detail as UncoveredDetail);
      }
    }
  }
  throw err;
}

export const paymentSplitApi = {
  list(orderId: string): Promise<ListSplitsResponse> {
    return apiFetch<ListSplitsResponse>(
      `${BASE}/orders/${encodeURIComponent(orderId)}/splits`,
    );
  },

  async create(input: CreateSplitInput): Promise<PaymentSplit> {
    try {
      return await apiFetch<PaymentSplit>(`${BASE}/splits`, {
        method: "POST",
        body: input,
      });
    } catch (err) {
      map409(err);
    }
  },

  async update(splitId: string, claimedLines: ClaimedLine[]): Promise<PaymentSplit> {
    try {
      return await apiFetch<PaymentSplit>(
        `${BASE}/splits/${encodeURIComponent(splitId)}`,
        { method: "PATCH", body: { claimedLines } },
      );
    } catch (err) {
      map409(err);
    }
  },

  async checkoutCard(splitId: string, input: CheckoutCardInput): Promise<IntentResponse> {
    try {
      return await apiFetch<IntentResponse>(
        `${BASE}/splits/${encodeURIComponent(splitId)}/checkout`,
        { method: "POST", body: input },
      );
    } catch (err) {
      map409(err);
    }
  },

  /** Customer asks the server to flip draft → awaiting_collection (cash). */
  markCashPending(splitId: string): Promise<PaymentSplit> {
    return apiFetch<PaymentSplit>(
      `${BASE}/splits/${encodeURIComponent(splitId)}/mark-cash-pending`,
      { method: "POST", body: {} },
    );
  },

  release(splitId: string, reason?: string): Promise<PaymentSplit> {
    return apiFetch<PaymentSplit>(
      `${BASE}/splits/${encodeURIComponent(splitId)}/release`,
      { method: "POST", body: reason ? { reason } : {} },
    );
  },
};

export type PaymentSplitApi = typeof paymentSplitApi;
